if (!customElements.get('product-info')) {
  // O'Cloudy configurator: in-memory cache of Section Rendering API responses,
  // keyed by request URL. Shared by every product-info instance on the page
  // (the element is replaced on each layout swap, the cache must survive that).
  const ocConfiguratorCache = new Map();
  // Same idea for the variant picker: Section Rendering API responses for
  // each colour (raw HTML text), keyed by request URL, plus the set of
  // gallery images already warmed into the browser cache.
  const ocSectionTextCache = new Map();
  const ocPreloadedImages = new Set();
  let ocRubikPresent;

  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      cartUpdateUnsubscriber = undefined;
      configuratorObserver = undefined;
      configuratorRequestToken = undefined;
      configuratorRewarmToken = undefined;

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }
        this.syncAddonVariantsToSelectedOptions(this.currentVariant);
        this.syncConfiguratorLinksToColor(this.currentVariant);

        this.addEventListener('click', this.handleConfiguratorLinkClick.bind(this));
        this.initConfiguratorPrefetch();
        this.initVariantPrefetch();

        this.addEventListener('change', (event) => {
          if (event.target.closest('[data-additional-product-selector]')) {
            this.syncAddonVariantsToSelectedOptions(this.currentVariant);
            this.scrollToGalleryOnMobile();
          }
        });

        this.handleVariantChangedEvent = (event) => {
          this.syncAddonVariantsToSelectedOptions(event.detail.variant);
          this.syncConfiguratorLinksToColor(event.detail.variant);
        };
        document.addEventListener('variant:changed', this.handleVariantChangedEvent);
      }

      // ---------------------------------------------------------------------
      // Colour carry-over across layouts (Sep 29, 2026).
      //
      // Pick 3 seater in Ocean Slate, tap 4 seater: the 4 seater should open
      // in Ocean Slate, not in its first colour. Each configurator link
      // (layout tile, Sofa / Sofa bed switch) carries data-color-variants,
      // a {"<colour, lowercase>": <variant id>} map of the TARGET product,
      // rendered by snippets/oc-configurator-color-link.liquid. Liquid already
      // points the href at the colour shown on page load; this keeps it in
      // step every time the shopper changes colour, by rewriting the link's
      // ?variant= to the target's variant in that colour. When the target
      // does not come in the colour, the param is dropped and the target
      // opens on its first available colour, as it always did. Such a tile
      // also gets .layout-option--no-color (no styling yet; a hook in case
      // we ever want to dim layouts that do not come in the chosen colour).
      //
      // The Section Rendering API honours ?variant= like a normal page load,
      // so the swapped <product-info> arrives with that colour selected,
      // its gallery, price and sticky bar already right. Colour is option1
      // on every sofa product, same rule the Ottoman add-on map relies on.
      // ---------------------------------------------------------------------

      static colorKeyOf(variant) {
        if (!variant) return '';
        const value = variant.option1 != null ? variant.option1 : variant.options && variant.options[0];
        return value == null ? '' : String(value).trim().toLowerCase();
      }

      static withVariantParam(href, variantId) {
        const hashIndex = href.indexOf('#');
        const hash = hashIndex === -1 ? '' : href.slice(hashIndex);
        const noHash = hashIndex === -1 ? href : href.slice(0, hashIndex);
        const queryIndex = noHash.indexOf('?');
        const path = queryIndex === -1 ? noHash : noHash.slice(0, queryIndex);
        const params = new URLSearchParams(queryIndex === -1 ? '' : noHash.slice(queryIndex + 1));
        params.delete('variant');
        if (variantId) params.set('variant', String(variantId));
        const query = params.toString();
        return `${path}${query ? `?${query}` : ''}${hash}`;
      }

      syncConfiguratorLinksToColor(variant) {
        const colorKey = ProductInfo.colorKeyOf(variant);
        if (!colorKey) return;

        let changed = false;
        this.querySelectorAll('[data-configurator-link][href][data-color-variants]').forEach((link) => {
          let map;
          try {
            map = JSON.parse(link.dataset.colorVariants);
          } catch (error) {
            return;
          }
          const variantId = map && Object.prototype.hasOwnProperty.call(map, colorKey) ? map[colorKey] : null;
          const href = link.getAttribute('href');
          const next = ProductInfo.withVariantParam(href, variantId);
          if (next !== href) {
            link.setAttribute('href', next);
            changed = true;
          }
          link.classList.toggle('layout-option--no-color', !variantId);
        });

        // The background prefetch (initConfiguratorPrefetch) warmed the tiles
        // for the colour shown at page load. After a colour change the tiles
        // point at new URLs, so warm those too, or the first tap after a
        // colour change would wait on the network. Same cache, same one-at-a-
        // time pacing; a newer colour change cancels the older loop.
        if (changed) this.rewarmConfiguratorTargets();
      }

      rewarmConfiguratorTargets() {
        if (!ProductInfo.connectionAllowsPrefetch()) return;
        const token = (this.configuratorRewarmToken = {});
        const run = () => {
          if (this.configuratorRewarmToken !== token || !this.isConnected) return;
          const targets = [];
          this.querySelectorAll('[data-configurator-link][href][data-color-variants]').forEach((link) => {
            const href = link.getAttribute('href');
            if (href && !this.isCurrentProductUrl(href) && !targets.includes(href)) targets.push(href);
          });
          let index = 0;
          const next = () => {
            if (this.configuratorRewarmToken !== token || !this.isConnected || index >= targets.length) return;
            this.fetchConfiguratorSections(targets[index++], { prefetch: true })
              .catch(() => {})
              .then(() => setTimeout(next, 150));
          };
          next();
        };
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(run, { timeout: 3000 });
        } else {
          setTimeout(run, 1000);
        }
      }

      syncAddonVariantsToSelectedOptions(variant) {
        if (!variant || !variant.options) return;

        const selectedOptionValues = variant.options.map((option) => option.toLowerCase());
        let hasCheckedMismatch = false;

        this.querySelectorAll('[data-additional-product-selector] input[data-variant-map]').forEach((radio) => {
          const addonOption = radio.closest('.addon-option');
          let variantMap;
          try {
            variantMap = JSON.parse(radio.dataset.variantMap);
          } catch (error) {
            return;
          }

          const matchedKey = Object.keys(variantMap).find((key) => selectedOptionValues.includes(key));
          const matchedEntry = matchedKey ? variantMap[matchedKey] : null;
          const isMismatch = !matchedEntry;
          const isUnavailable = !!matchedEntry && !matchedEntry.available;

          if (matchedEntry) {
            radio.value = matchedEntry.id;
          }

          addonOption?.classList.toggle('addon-option--mismatch', isMismatch);
          addonOption?.classList.toggle('addon-option--unavailable', isUnavailable);
          if ((isMismatch || isUnavailable) && radio.checked) hasCheckedMismatch = true;
        });

        const errorElement = this.querySelector('[data-additional-product-error]');
        if (errorElement) errorElement.hidden = !hasCheckedMismatch;

        // Mirror the add-on mismatch state onto BOTH the main submit and the
        // sticky bar submit, so the two buttons never disagree.
        const submitButtons = [
          this.productForm?.querySelector('[type="submit"]'),
          ...document.querySelectorAll('#shopify-section-sticky-atc-bar form[is="product-form"] [type="submit"]'),
        ].filter(Boolean);

        submitButtons.forEach((submitButton) => {
          if (hasCheckedMismatch) {
            submitButton.setAttribute('disabled', '');
            submitButton.setAttribute('aria-disabled', 'true');
            submitButton.dataset.disabledByAddon = 'true';
          } else if (submitButton.dataset.disabledByAddon === 'true') {
            // Only clear disabled/aria-disabled if we were the ones who set them.
            // The main variant itself may already be sold out and disabled by
            // the theme's own variant-swap logic — don't undo that.
            submitButton.removeAttribute('disabled');
            submitButton.removeAttribute('aria-disabled');
            delete submitButton.dataset.disabledByAddon;
          }
        });

        if (!hasCheckedMismatch) {
          const formErrorMessage = this.productForm?.querySelector('.product-form__error-message');
          if (formErrorMessage && !formErrorMessage.hidden) {
            formErrorMessage.hidden = true;
            formErrorMessage.innerText = '';
          }
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.configuratorObserver?.disconnect();
        document.removeEventListener('variant:changed', this.handleVariantChangedEvent);
      }

      // ---------------------------------------------------------------------
      // O'Cloudy configurator (layout tiles, Sofa / Sofa bed switch).
      //
      // Sep 24, 2026 speed pass. Before: every tile click fetched the ENTIRE
      // product page (~600 KB), parsed it and swapped <main>, then fetched the
      // whole page AGAIN to refresh the Rubik Variant Images app (which is no
      // longer installed; the URL of that second fetch was also malformed).
      // Measured on desktop: ~600 ms server + ~300 ms parse/swap + ~450 ms
      // wasted second fetch. On a phone the 600 KB of HTML alone is 2-3 s.
      //
      // Now:
      //   1. The Section Rendering API returns only the main product section
      //      and the sticky add-to-cart bar (~130 KB), one request, and only
      //      <product-info> and the sticky bar are swapped. Everything else on
      //      the product templates is static per model, so nothing is lost.
      //      Oct 14, 2026: no longer quite true. The O'Cloudy PDP galleries
      //      (sections oc_gallery for the sofas, oc_gallery_bed for the sofa
      //      beds) are limited to product handles, so they are fetched and
      //      swapped as well; see configuratorGallerySectionIds.
      //   2. Responses are prefetched into memory: immediately on hover /
      //      touch / keyboard focus of a tile, and in the background, one at a
      //      time, once the configurator has scrolled into view and the page
      //      is idle. A click on a warmed tile swaps with no network wait.
      //      Skipped on Save-Data and 2G connections.
      //   3. The Rubik refresh only runs if the Rubik script is actually on
      //      the page.
      //   4. The clicked tile gets .is-loading / aria-busy while it waits.
      // ---------------------------------------------------------------------

      // Sections below the buy box that change with the product (Oct 14, 2026).
      // The O'Cloudy PDP galleries (`oc_gallery` for the sofas, `oc_gallery_bed`
      // for the sofa beds) are limited to product handles, so they have to be
      // re-rendered on every layout swap or the shopper keeps the gallery of
      // whichever product the page loaded with. Their section IDs share the
      // main section's template prefix (template--<id>__oc_gallery...). Only
      // wrappers actually present in the DOM are requested, so a template
      // without these galleries asks for nothing extra.
      get configuratorGallerySectionIds() {
        const prefix = this.sectionId.replace(/__main$/, '');
        if (!prefix || prefix === this.sectionId) return [];
        return Array.from(document.querySelectorAll(`[id^="shopify-section-${prefix}__oc_gallery"]`)).map((el) =>
          el.id.replace(/^shopify-section-/, '')
        );
      }

      get configuratorSectionIds() {
        const ids = [this.sectionId];
        if (document.getElementById('shopify-section-sticky-atc-bar')) ids.push('sticky-atc-bar');
        ids.push(...this.configuratorGallerySectionIds);
        return ids;
      }

      // Replace each gallery section wrapper with its freshly rendered copy.
      // A gallery hidden for this product renders as an empty wrapper, so the
      // old one disappears. Inline <script>s in markup inserted via DOMParser
      // never run, so they are re-created: the oc-lazy-video snippet's script
      // is what starts the autoplay tiles, and it is idempotent.
      swapConfiguratorGallerySections(sections) {
        this.configuratorGallerySectionIds.forEach((id) => {
          const oldSection = document.getElementById(`shopify-section-${id}`);
          if (!oldSection) return;
          const html = sections[id];
          if (!html) {
            oldSection.innerHTML = '';
            return;
          }
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newSection = doc.getElementById(`shopify-section-${id}`) || doc.body.firstElementChild;
          if (!newSection) return;
          oldSection.replaceWith(newSection);
          newSection.querySelectorAll('script').forEach((inert) => {
            const script = document.createElement('script');
            Array.from(inert.attributes).forEach((attr) => script.setAttribute(attr.name, attr.value));
            script.textContent = inert.textContent;
            inert.replaceWith(script);
          });
          if (window.ocLazyVideo) window.ocLazyVideo.observe(newSection);
        });
      }

      configuratorRequestUrl(productUrl) {
        const separator = productUrl.includes('?') ? '&' : '?';
        return `${productUrl}${separator}sections=${this.configuratorSectionIds.join(',')}`;
      }

      isCurrentProductUrl(productUrl) {
        return productUrl.split('?')[0].split('#')[0] === this.dataset.url;
      }

      fetchConfiguratorSections(productUrl, { prefetch = false } = {}) {
        const requestUrl = this.configuratorRequestUrl(productUrl);
        if (ocConfiguratorCache.has(requestUrl)) return ocConfiguratorCache.get(requestUrl);

        const options = prefetch ? { priority: 'low' } : {};
        const request = fetch(requestUrl, options)
          .then((response) => {
            if (!response.ok) throw new Error(`[configurator] HTTP ${response.status} for ${requestUrl}`);
            return response.json();
          })
          .catch((error) => {
            ocConfiguratorCache.delete(requestUrl);
            throw error;
          });

        ocConfiguratorCache.set(requestUrl, request);
        return request;
      }

      static connectionAllowsPrefetch() {
        const connection = navigator.connection;
        if (!connection) return true;
        if (connection.saveData) return false;
        return !/(^|[^0-9])2g$/.test(connection.effectiveType || '');
      }

      initConfiguratorPrefetch() {
        const links = Array.from(this.querySelectorAll('[data-configurator-link][href]'));
        if (!links.length) return;
        if (!ProductInfo.connectionAllowsPrefetch()) return;

        if (!document.getElementById('oc-configurator-loading-style')) {
          const style = document.createElement('style');
          style.id = 'oc-configurator-loading-style';
          style.textContent =
            '[data-configurator-link].is-loading{opacity:.5;cursor:progress;transition:opacity .15s ease}';
          document.head.appendChild(style);
        }

        const targets = [];
        links.forEach((link) => {
          const href = link.getAttribute('href');
          if (href && !this.isCurrentProductUrl(href) && !targets.includes(href)) targets.push(href);
        });
        if (!targets.length) return;

        const warm = (href) => this.fetchConfiguratorSections(href, { prefetch: true }).catch(() => {});

        // Intent: hover (desktop), touch (mobile), keyboard focus.
        const onIntent = (event) => {
          const link = event.target.closest && event.target.closest('[data-configurator-link][href]');
          if (!link || !this.contains(link)) return;
          const href = link.getAttribute('href');
          if (!href || this.isCurrentProductUrl(href)) return;
          warm(href);
        };
        this.addEventListener('pointerover', onIntent, { passive: true });
        this.addEventListener('touchstart', onIntent, { passive: true, capture: true });
        this.addEventListener('focusin', onIntent);

        // Background: once the tiles are near the viewport and the page has
        // finished loading, warm the remaining layouts one at a time.
        const warmAll = () => {
          let index = 0;
          const next = () => {
            if (!this.isConnected || index >= targets.length) return;
            warm(targets[index++]).then(() => setTimeout(next, 150));
          };
          next();
        };
        const whenIdle = () => {
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(warmAll, { timeout: 4000 });
          } else {
            setTimeout(warmAll, 1500);
          }
        };
        const schedule = () => {
          if (document.readyState === 'complete') {
            whenIdle();
          } else {
            window.addEventListener('load', whenIdle, { once: true });
          }
        };

        const grid = links[0].closest('.layout-grid, .configurator-content, .configurator') || links[0];
        if ('IntersectionObserver' in window) {
          this.configuratorObserver = new IntersectionObserver(
            (entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              this.configuratorObserver.disconnect();
              schedule();
            },
            { rootMargin: '200px 0px' }
          );
          this.configuratorObserver.observe(grid);
        } else {
          schedule();
        }
      }

      handleConfiguratorLinkClick(event) {
        const link = event.target.closest('[data-configurator-link][href]');
        if (!link || !this.contains(link)) return;
        if (event.defaultPrevented) return;
        // Let "open in new tab" and similar modified clicks through untouched.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) return;

        const productUrl = link.getAttribute('href');
        if (!productUrl) return;

        event.preventDefault();
        if (this.isCurrentProductUrl(productUrl)) return;

        this.disableButtons();
        link.classList.add('is-loading');
        link.setAttribute('aria-busy', 'true');

        const viewMode = this.dataset.viewMode || 'main-product';
        const scrollY = window.scrollY;
        const requestToken = (this.configuratorRequestToken = {});
        const swapProduct = this.handleSwapProduct(productUrl, false, viewMode);
        const oldTitle = this.querySelector('h1')?.textContent.trim();

        this.fetchConfiguratorSections(productUrl)
          .then((sections) => {
            // A later click on another tile wins; this element may also have
            // been swapped out already.
            if (this.configuratorRequestToken !== requestToken || !this.isConnected) return;

            const mainHtml = sections[this.sectionId];
            if (!mainHtml) throw new Error('[configurator] main section missing from response');
            const html = new DOMParser().parseFromString(mainHtml, 'text/html');
            if (!html.querySelector('product-info')) throw new Error('[configurator] product-info missing from response');

            // Swap the product FIRST so the rebuilt sticky bar (below) finds the
            // new main form when it connects, instead of falling back to its own
            // bare form (which cannot see the Ottoman add-on).
            swapProduct(html);

            const stickyHtml = sections['sticky-atc-bar'];
            const oldSticky = document.getElementById('shopify-section-sticky-atc-bar');
            if (stickyHtml && oldSticky) {
              const stickyDoc = new DOMParser().parseFromString(stickyHtml, 'text/html');
              const newSticky =
                stickyDoc.getElementById('shopify-section-sticky-atc-bar') || stickyDoc.body.firstElementChild;
              if (newSticky) {
                HTMLUpdateUtility.viewTransition(
                  oldSticky,
                  newSticky,
                  this.preProcessHtmlCallbacks,
                  this.postProcessHtmlCallbacks
                );
              }
            }

            // Galleries under the buy box depend on the product handle.
            this.swapConfiguratorGallerySections(sections);

            const newTitle = html.querySelector('h1')?.textContent.trim();
            if (oldTitle && newTitle && document.title.includes(oldTitle)) {
              document.title = document.title.replace(oldTitle, newTitle);
            }

            window.scrollTo({ top: scrollY, behavior: 'auto' });
            this.scrollToGalleryOnMobile();
          })
          .catch((error) => {
            console.warn('[configurator] swap failed, navigating instead', error);
            window.location.href = productUrl;
          });
      }

      // On mobile, a layout / color / ottoman selection happens far below the
      // gallery, so the person never sees the image react to their choice -
      // the page just stays parked on the configurator. Bring the gallery
      // back into view after the update. Desktop keeps the stay-in-place
      // behavior (the gallery sits beside the options there), and quick-view
      // popups are left alone.
      scrollToGalleryOnMobile() {
        try {
          if ((this.dataset.viewMode || 'main-product') === 'quick-view') return;

          let isMobile;
          try {
            isMobile = window.matchMedia(FoxTheme.config.mediaQueryMobile).matches;
          } catch (error) {
            isMobile = window.matchMedia('(max-width: 767px)').matches;
          }
          if (!isMobile) return;

          // The configurator swap replaces <product-info> wholesale, so resolve
          // the gallery fresh from the document - never from a cached reference.
          const gallery =
            document.querySelector('product-info media-gallery') ||
            document.querySelector('[id^="MediaGallery-"]') ||
            document.querySelector('media-gallery');
          if (!gallery) return;

          // Account for a sticky/fixed header overlapping the top edge.
          let headerOffset = 0;
          ['sticky-header', '#shopify-section-header', '.site-header', 'header'].some((selector) => {
            const el = document.querySelector(selector);
            if (!el) return false;
            const position = window.getComputedStyle(el).position;
            if (position === 'sticky' || position === 'fixed') {
              headerOffset = el.offsetHeight;
              return true;
            }
            return false;
          });

          const rect = gallery.getBoundingClientRect();

          // Already looking at it? Don't move the page.
          if (rect.top >= 0 && rect.top < window.innerHeight * 0.4) return;

          const top = Math.max(0, rect.top + window.scrollY - headerOffset - 8);
          window.scrollTo({ top: top, left: 0, behavior: 'smooth' });
        } catch (error) {}
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      // Rubik Variant Images (app embed block) is rendered by the theme layout
      // (header/footer), not inside any section, so it never comes back from
      // the section_id fetches used elsewhere in this file. Its script only
      // calls initVariantImageHandler(...) inside a 'DOMContentLoaded' listener,
      // which already fired once for the real page load and never fires again,
      // so on every AJAX product swap Rubik never re-filters for the new
      // product. Fetch the full page once per swap (with the resolved variant
      // in the URL, since Rubik resolves the selected variant from the URL),
      // pull out that one script with fresh per-product data, strip the
      // DOMContentLoaded wrapper, and run it immediately.
      //
      // Only runs when the Rubik script is actually present on the page
      // (checked once); the app is currently uninstalled.
      static rubikPresent() {
        if (ocRubikPresent === undefined) {
          ocRubikPresent = Array.from(document.scripts).some(
            (script) => !script.src && script.textContent.includes('initVariantImageHandler(')
          );
        }
        return ocRubikPresent;
      }

      refreshRubikVariantImages(productUrl, variantId) {
        const fetchUrl = ProductInfo.withVariantParam(productUrl, variantId);
        fetch(fetchUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const doc = new DOMParser().parseFromString(responseText, 'text/html');
            const rubikScript = Array.from(doc.querySelectorAll('script')).find((s) =>
              s.textContent.includes('initVariantImageHandler(')
            );
            if (!rubikScript) return;

            let text = rubikScript.textContent;
            const marker = 'document.addEventListener(';
            let searchFrom = 0;
            let statementStart = -1;
            while (true) {
              const idx = text.indexOf(marker, searchFrom);
              if (idx === -1) break;
              if (/DOMContentLoaded/.test(text.slice(idx, idx + 200))) {
                statementStart = idx;
                break;
              }
              searchFrom = idx + marker.length;
            }
            if (statementStart === -1) return;

            const arrowIdx = text.indexOf('=>', statementStart);
            const openBraceIdx = text.indexOf('{', arrowIdx);
            if (arrowIdx === -1 || openBraceIdx === -1) return;

            let depth = 0;
            let bodyEndIdx = -1;
            for (let i = openBraceIdx; i < text.length; i++) {
              if (text[i] === '{') depth++;
              else if (text[i] === '}') {
                depth--;
                if (depth === 0) {
                  bodyEndIdx = i;
                  break;
                }
              }
            }
            if (bodyEndIdx === -1) return;

            const closeParenIdx = text.indexOf(')', bodyEndIdx);
            if (closeParenIdx === -1) return;

            let statementEnd = closeParenIdx + 1;
            if (text[statementEnd] === ';') statementEnd++;

            const body = text.slice(openBraceIdx + 1, bodyEndIdx).trim();
            const rewritten = text.slice(0, statementStart) + body + text.slice(statementEnd);

            const scriptEl = document.createElement('script');
            scriptEl.textContent = rewritten;
            document.body.appendChild(scriptEl);
            scriptEl.remove();
          })
          .catch((error) => console.warn('[RUBIK] refresh fetch failed', error));
      }

      // ---------------------------------------------------------------------
      // Colour swatches: instant image change.
      //
      // Sep 24, 2026. Before, a colour click was: fetch the section for the
      // new variant (~300-400 ms), update price etc., then 150 ms later a
      // second script fetched the SAME section again (~400 ms) only to pull
      // the colour-filtered gallery out of it, swap the gallery, and only
      // then did the new colour's image start downloading. About 1 s on a
      // fast desktop, 2 s+ on a phone.
      //
      // Now:
      //   1. The gallery is swapped straight from the first response
      //      (swapColorGallery), so the second fetch is never needed.
      //   2. Each colour's section response is prefetched into memory on
      //      idle and on hover / touch / focus of its swatch, and
      //      renderProductInfo serves the click from that cache.
      //   3. From each prefetched response the colour's featured gallery
      //      image is warmed into the browser cache at the exact srcset
      //      candidate the gallery will ask for, so the swap paints at once.
      // ---------------------------------------------------------------------

      fetchSectionText(requestUrl, { prefetch = false } = {}) {
        if (ocSectionTextCache.has(requestUrl)) return ocSectionTextCache.get(requestUrl);
        const options = prefetch ? { priority: 'low' } : {};
        const request = fetch(requestUrl, options)
          .then((response) => {
            if (!response.ok) throw new Error(`[variant-prefetch] HTTP ${response.status} for ${requestUrl}`);
            return response.text();
          })
          .catch((error) => {
            ocSectionTextCache.delete(requestUrl);
            throw error;
          });
        ocSectionTextCache.set(requestUrl, request);
        return request;
      }

      // The request URL the theme would build if this input were picked,
      // using the same helper as the real click so the cache key matches.
      variantRequestUrlFor(input) {
        const live = this.variantSelectors;
        if (!live) return null;
        const fieldset = input.closest('fieldset');
        const values = Array.from(live.querySelectorAll('select option[selected], fieldset input:checked')).map((el) =>
          fieldset && el.closest('fieldset') === fieldset ? input.dataset.optionValueId : el.dataset.optionValueId
        );
        if (values.some((v) => !v)) return null;
        const productUrl = input.dataset.productUrl || this.dataset.url;
        return this.buildRequestUrlWithParams(productUrl, values, false);
      }

      // Warm the gallery image that will show for the variant in a section
      // response: the variant's featured media slide (or the first slide),
      // with the response's own srcset/sizes so the browser resolves the
      // same candidate URL it will pick for the real <img>.
      preloadGalleryImageFrom(responseText) {
        if (!responseText) return;
        const doc = new DOMParser().parseFromString(responseText, 'text/html');
        const gallery = doc.querySelector('media-gallery');
        if (!gallery) return;
        let slideImg = null;
        try {
          const variant = this.getSelectedVariant(doc);
          const mediaId = variant && variant.featured_media && variant.featured_media.id;
          if (mediaId != null) {
            slideImg = gallery.querySelector(`.product__media-item[data-media-id="${mediaId}"] img`);
          }
        } catch (error) {}
        if (!slideImg) slideImg = gallery.querySelector('.product__media-item img');
        if (!slideImg) return;
        const src = slideImg.getAttribute('src');
        const srcset = slideImg.getAttribute('srcset');
        const key = srcset || src;
        if (!key || ocPreloadedImages.has(key)) return;
        ocPreloadedImages.add(key);
        const liveImg = this.querySelector('media-gallery .product__media-item img');
        const sizes = slideImg.getAttribute('sizes') || (liveImg && liveImg.getAttribute('sizes')) || '';
        const warm = new Image();
        try {
          warm.fetchPriority = 'low';
        } catch (error) {}
        if (sizes) warm.sizes = sizes;
        if (srcset) warm.srcset = srcset;
        if (src) warm.src = src;
      }

      initVariantPrefetch() {
        const live = this.variantSelectors;
        if (!live) return;
        const inputs = Array.from(live.querySelectorAll('fieldset input[type="radio"][data-option-value-id]'));
        if (!inputs.length) return;
        if (!ProductInfo.connectionAllowsPrefetch()) return;

        const warm = (input) => {
          if (!input || input.checked) return Promise.resolve();
          const url = this.variantRequestUrlFor(input);
          if (!url) return Promise.resolve();
          return this.fetchSectionText(url, { prefetch: true })
            .then((text) => this.preloadGalleryImageFrom(text))
            .catch(() => {});
        };

        // Intent: hover (desktop), touch (mobile), keyboard focus on a swatch.
        const onIntent = (event) => {
          const target = event.target;
          if (!target || !target.closest) return;
          let input = target.closest('input[type="radio"][data-option-value-id]');
          if (!input) {
            const label = target.closest('label');
            if (label) {
              input = label.control || (label.htmlFor ? document.getElementById(label.htmlFor) : null);
              if (!input) input = label.querySelector('input[type="radio"][data-option-value-id]');
            }
          }
          if (!input || !this.contains(input) || !input.dataset.optionValueId) return;
          warm(input);
        };
        this.addEventListener('pointerover', onIntent, { passive: true });
        this.addEventListener('touchstart', onIntent, { passive: true, capture: true });
        this.addEventListener('focusin', onIntent);

        // Background: once the page has loaded and is idle, warm every
        // other option value one at a time (re-read live: the picker is
        // re-rendered on each change).
        const warmAll = () => {
          const current = this.variantSelectors;
          if (!this.isConnected || !current) return;
          const queue = Array.from(current.querySelectorAll('fieldset input[type="radio"][data-option-value-id]')).filter(
            (input) => !input.checked
          );
          let index = 0;
          const next = () => {
            if (!this.isConnected || index >= queue.length) return;
            warm(queue[index++]).then(() => setTimeout(next, 120));
          };
          next();
        };
        const whenIdle = () => {
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(warmAll, { timeout: 4000 });
          } else {
            setTimeout(warmAll, 1500);
          }
        };
        if (document.readyState === 'complete') {
          whenIdle();
        } else {
          window.addEventListener('load', whenIdle, { once: true });
        }
      }

      // The gallery is server-rendered for ONE colour (see
      // snippets/product-media-gallery.liquid). A variant response already
      // carries the gallery for the new colour, so swap it in right away
      // instead of letting that snippet fetch the whole section a second time.
      swapColorGallery(html) {
        const fresh = html.querySelector('media-gallery[data-rendered-color]');
        const live = this.querySelector('media-gallery[data-rendered-color]');
        if (!fresh || !live) return false;
        if ((fresh.dataset.renderedColor || '') === (live.dataset.renderedColor || '')) return false;
        live.replaceWith(document.importNode(fresh, true));
        return true;
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.disableButtons();
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );

            if (!variant) {
              this.setUnavailable();
              return;
            }
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;

          if (variant && ProductInfo.rubikPresent()) {
            this.refreshRubikVariantImages(productUrl, variant.id);
          }
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.swapColorGallery(html);
          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku');
          updateSourceFromDestination('Inventory');
          updateSourceFromDestination('Badges');
          updateSourceFromDestination('PricePerItem');
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          updateSourceFromDestination('QuantityRules');
          updateSourceFromDestination('VolumeNote');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);
          if (stickyAtcBar) {
            stickyAtcBar.classList.remove('hidden');
          }

          const newAddButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const isDisabled = !newAddButton || newAddButton.hasAttribute('disabled');
          this.updateButtonsState(isDisabled, {
            updateText: true,
            text: isDisabled ? FoxTheme.variantStrings.soldOut : null,
          });

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          this.currentVariant = variant;

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: variant,
              },
            })
          );

          this.scrollToGalleryOnMobile();
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        if (!params.length) return url;

        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const token = (this.renderToken = {});

        // A prefetched (or in-flight prefetch) response is used as is; a
        // cached promise cannot be aborted, so a newer click simply wins
        // through the token check below.
        const request = ocSectionTextCache.has(requestUrl)
          ? ocSectionTextCache.get(requestUrl)
          : fetch(requestUrl, { signal: this.abortController.signal }).then((response) => response.text());

        request
          .then((responseText) => {
            if (this.renderToken !== token || !this.isConnected) return;
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        // Configurator links already carry ?variant= (colour carry-over), so
        // replace any existing variant param instead of appending a second one.
        window.history.replaceState({}, '', ProductInfo.withVariantParam(url, variantId));
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      updateButtonsState(disabled, options = {}) {
        const { updateText = false, text = null, updateStyles = true } = options;

        // Handle main add button in product form
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (productForm) {
          const addButton = productForm.querySelector('[name="add"]');
          const addButtonText = productForm.querySelector('[name="add"] > span');

          if (addButton) {
            if (disabled) {
              addButton.setAttribute('disabled', 'disabled');
              if (updateText && text && addButtonText) {
                addButtonText.textContent = this.decoded(text);
              }
            } else {
              const variantId = productForm.querySelector('[name="id"]')?.value;
              if (variantId) {
                addButton.removeAttribute('disabled');
                if (updateText && addButtonText) {
                  addButtonText.textContent = this.decoded(FoxTheme.variantStrings.addToCart);
                }
              }
            }

            if (updateStyles) {
              addButton.style.pointerEvents = disabled ? 'none' : '';
              addButton.style.opacity = disabled ? '0.6' : '';
            }
          }
        }

        // Handle sticky ATC button
        const otherButtonSelectors = ['.sticky-atc-bar [name="add"]'];
        otherButtonSelectors.forEach((selector) => {
          const buttons = document.querySelectorAll(selector);
          buttons.forEach((button) => {
            if (disabled) {
              button.setAttribute('disabled', 'disabled');
              if (updateStyles) {
                button.style.pointerEvents = 'none';
                button.style.opacity = '0.6';
              }
            } else {
              button.removeAttribute('disabled');
              if (updateStyles) {
                button.style.pointerEvents = '';
                button.style.opacity = '';
              }
            }
          });
        });
      }

      decoded(text) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        const decoded = tempDiv.textContent;
        return decoded;
      }

      setUnavailable() {
        this.updateButtonsState(true, {
          updateText: true,
          text: FoxTheme.variantStrings.unavailable,
        });
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);
        const volumePricing = document.getElementById(`Volume-${this.dataset.section}`);
        const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
        if (volumePricing) volumePricing.classList.add('hidden');
        if (stickyAtcBar) stickyAtcBar.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }

      disableButtons() {
        this.updateButtonsState(true);
      }

      enableButtons() {
        this.updateButtonsState(false);
      }
    }
  );
}
